import { GamesDict, SportType } from '@/types';

const SPORT_LABELS: Record<SportType, string> = {
  nba: 'NBA',
  soccer: 'soccer',
  nfl: 'NFL',
  mlb: 'MLB',
  nhl: 'NHL',
};

// Completed-game count decides first. Search demand breaks ties so feed object
// order can never silently change the homepage destination.
export const HOME_SPORT_TIE_BREAK_PRIORITY: readonly SportType[] = [
  'soccer',
  'mlb',
  'nba',
  'nhl',
  'nfl',
];

export type HomeSportDestination =
  | {
      kind: 'sport';
      sport: SportType;
      path: `/sport/${SportType}`;
      label: string;
      supportingText: string;
      completedGameCount: number;
    }
  | {
      kind: 'fallback';
      path: '#available-sports';
      label: 'Browse sports';
      supportingText: 'Check each sport for current completed-game availability.';
      completedGameCount: 0;
    };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const countCompletedGames = (games: unknown): number => {
  if (!isRecord(games)) {
    return 0;
  }

  return Object.values(games).filter((game) => {
    if (!isRecord(game) || !isRecord(game.status)) {
      return false;
    }

    return game.status.type === 'completed';
  }).length;
};

export const resolveHomeSportDestination = (
  games: GamesDict | null | undefined,
): HomeSportDestination => {
  if (!isRecord(games)) {
    return {
      kind: 'fallback',
      path: '#available-sports',
      label: 'Browse sports',
      supportingText: 'Check each sport for current completed-game availability.',
      completedGameCount: 0,
    };
  }

  let selectedSport: SportType | null = null;
  let selectedCount = 0;

  for (const sport of HOME_SPORT_TIE_BREAK_PRIORITY) {
    const completedGameCount = countCompletedGames(games[sport]);
    if (completedGameCount > selectedCount) {
      selectedSport = sport;
      selectedCount = completedGameCount;
    }
  }

  if (!selectedSport) {
    return {
      kind: 'fallback',
      path: '#available-sports',
      label: 'Browse sports',
      supportingText: 'Check each sport for current completed-game availability.',
      completedGameCount: 0,
    };
  }

  const label = SPORT_LABELS[selectedSport];
  return {
    kind: 'sport',
    sport: selectedSport,
    path: `/sport/${selectedSport}`,
    label: `Choose ${label} replays`,
    supportingText: `${selectedCount} completed ${selectedCount === 1 ? 'game' : 'games'} ready to explore.`,
    completedGameCount: selectedCount,
  };
};
